import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Send, X, CheckCheck, Volume2, VolumeX } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { notificationsApi, PendingNotification } from '@/api/notificationsApi';
import { useSignalR } from '@/hooks/useSignalR';
import { usePermissions } from '@/hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import Toast from '@/components/ui/Toast';

export function NotificationCenter() {
  const [open, setOpen]             = useState(false);
  const [items, setItems]           = useState<PendingNotification[]>([]);
  const [loading, setLoading]       = useState(false);
  const [sending, setSending]       = useState<string | null>(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [inAppUnread, setInAppUnread] = useState(0);
  const panelRef                    = useRef<HTMLDivElement>(null);
  const { on, off }                 = useSignalR();
  const permissions                 = usePermissions();
  const navigate                    = useNavigate();
  const { soundEnabled, toggleSound, playNotificationSound } = useNotificationSound();
  const [liveAlert, setLiveAlert]   = useState<string | null>(null);

  function load() {
    setLoading(true);
    if (!permissions.isManagerOrAbove) {
      setItems([]);
      setLoading(false);
      return;
    }
    notificationsApi.getPending()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  function loadUnreadCount() {
    notificationsApi.list(true)
      .then((items) => setInAppUnread(items.length))
      .catch(() => {});
  }

  useEffect(() => {
    load();
    loadUnreadCount();
    const smsId = setInterval(load, 30_000);
    const unreadId = setInterval(loadUnreadCount, 30_000);
    return () => { clearInterval(smsId); clearInterval(unreadId); };
  }, [permissions.isManagerOrAbove]);

  const handleNewIntake = useCallback(() => {
    notificationsApi.list(true).then((notifications) => setInAppUnread(notifications.length)).catch(() => {});
    load();
    setLiveAlert('New shop notification received');
    void playNotificationSound();
  }, [playNotificationSound]);

  useEffect(() => {
    const events = [
      'NewIntakeReceived', 'InvoiceGenerated', 'WorkOrderCompleted',
      'AppointmentConfirmed', 'AppointmentCancelled', 'EstimateSent',
      'EstimateApproved', 'EstimateRejected', 'EstimateConverted',
    ];
    events.forEach((event) => on(event, handleNewIntake));
    return () => events.forEach((event) => off(event, handleNewIntake));
  }, [on, off, handleNewIntake]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function handleSend(id: string) {
    setSending(id);
    try {
      await notificationsApi.send(id);
      setItems((prev) => prev.filter((n) => n.notificationId !== id));
    } catch {}
    finally { setSending(null); }
  }

  async function handleCancel(id: string) {
    setSending(id);
    try {
      await notificationsApi.cancel(id);
      setItems((prev) => prev.filter((n) => n.notificationId !== id));
    } catch {}
    finally { setSending(null); }
  }

  async function handleSendAll() {
    setSendingAll(true);
    try {
      await notificationsApi.sendBatch();
      load();
    } catch {}
    finally { setSendingAll(false); }
  }

  return (
    <div className="relative" ref={panelRef}>
      {liveAlert && <Toast message={liveAlert} type="info" duration={6000} onClose={() => setLiveAlert(null)} />}
      <button
        onClick={() => { setOpen((o) => !o); if (!open) { load(); loadUnreadCount(); } }}
        className="relative p-2 rounded-xl bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/20 transition-colors"
        aria-label="Notification center"
      >
        <Bell className="h-5 w-5" />
        {items.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
            {items.length > 99 ? '99+' : items.length}
          </span>
        )}
        {items.length === 0 && inAppUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark rounded-2xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-border-dark">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Notifications {items.length > 0 && <span className="text-neutral-400 font-normal">· {items.length} SMS queued</span>}
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                title={soundEnabled ? 'Notification sound on' : 'Notification sound off'}
                aria-label={soundEnabled ? 'Turn notification sound off' : 'Turn notification sound on'}
                onClick={() => {
                  const enabling = !soundEnabled;
                  toggleSound();
                  if (enabling) void playNotificationSound(true);
                }}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"
              >
                {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </button>
            {items.length > 0 && (
              <button
                onClick={handleSendAll}
                disabled={sendingAll}
                className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 disabled:opacity-50 transition-colors"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {sendingAll ? 'Sending…' : 'Send All'}
              </button>
            )}
            </div>
          </div>

          {/* In-app unread alert strip */}
          {inAppUnread > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 dark:bg-orange-500/10 border-b border-orange-100 dark:border-orange-500/20">
              <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
              <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 flex-1">
                {inAppUnread} unread notification{inAppUnread > 1 ? 's' : ''}
              </p>
              <button
                onClick={() => { setOpen(false); navigate('/notifications'); }}
                className="text-[10px] text-orange-500 hover:text-orange-700 font-semibold"
              >
                View
              </button>
            </div>
          )}

          {/* Body */}
          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="text-xs text-neutral-400 text-center py-8">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-xs text-neutral-400 text-center py-8">No pending SMS messages</p>
            ) : (
              items.map((n) => (
                <div key={n.notificationId} className="px-4 py-3 border-b border-slate-100 dark:border-border-dark last:border-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{n.customerName}</p>
                      <p className="text-xs text-neutral-400">{n.workOrderNumber} · {n.phoneNumber}</p>
                    </div>
                    <span className="text-xs text-neutral-400 flex-shrink-0">
                      {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-2 line-clamp-2">{n.message}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSend(n.notificationId)}
                      disabled={sending === n.notificationId}
                      className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 disabled:opacity-50 transition-colors"
                    >
                      <Send className="h-3 w-3" />
                      {sending === n.notificationId ? 'Sending…' : 'Send'}
                    </button>
                    <button
                      onClick={() => handleCancel(n.notificationId)}
                      disabled={sending === n.notificationId}
                      className="flex items-center gap-1 text-xs font-semibold text-neutral-400 hover:text-red-500 disabled:opacity-50 transition-colors"
                    >
                      <X className="h-3 w-3" /> Cancel
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
